import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'

const StoragePage: React.FC = () => {
  const [showConfirmPurge, setShowConfirmPurge] = useState(false)
  const [purgeResult, setPurgeResult] = useState<{ success: boolean; message: string; deletedFiles: number } | null>(null)

  const queryClient = useQueryClient()

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: adminApi.getMetrics,
  })

  const purgeMutation = useMutation({
    mutationFn: adminApi.purgeStorage,
    onSuccess: (data) => {
      setPurgeResult(data)
      setShowConfirmPurge(false)
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] })
    },
    onError: (error: any) => {
      setPurgeResult({
        success: false,
        message: error.response?.data?.error?.message || 'Failed to purge storage',
        deletedFiles: 0,
      })
      setShowConfirmPurge(false)
    },
  })

  const handlePurgeStorage = () => {
    purgeMutation.mutate()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Storage Management</h1>
      </div>

      {/* Storage Overview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Storage Overview
          </h3>
          {metricsLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="bg-gray-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Total Storage Used</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {metrics?.storageUsed || 'N/A'}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Total Screenshots</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {metrics?.totalScreenshots?.toLocaleString() || '0'}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Last Cleanup</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {metrics?.lastCleanup 
                    ? new Date(metrics.lastCleanup).toLocaleString()
                    : 'Never'
                  }
                </dd>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Purge Result */}
      {purgeResult && (
        <div className={`rounded-md p-4 ${
          purgeResult.success ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className={`text-sm ${
            purgeResult.success ? 'text-green-700' : 'text-red-700'
          }`}>
            <strong>{purgeResult.success ? 'Success!' : 'Error!'}</strong>
            <br />
            {purgeResult.message}
            {purgeResult.success && purgeResult.deletedFiles > 0 && (
              <>
                <br />
                Deleted {purgeResult.deletedFiles} files.
              </>
            )}
          </div>
          <button
            onClick={() => setPurgeResult(null)}
            className={`mt-2 text-sm underline ${
              purgeResult.success ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
            }`}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Storage Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Storage Actions
          </h3>
          
          <div className="space-y-4">
            {/* Manual Purge */}
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-lg">⚠️</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-sm font-medium text-red-800">
                    Purge All Storage
                  </h4>
                  <p className="mt-1 text-sm text-red-700">
                    This will permanently delete all screenshots and associated data from R2 storage. 
                    This action cannot be undone.
                  </p>
                  <div className="mt-4">
                    {!showConfirmPurge ? (
                      <button
                        onClick={() => setShowConfirmPurge(true)}
                        className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Purge Storage
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-red-800">
                          Are you absolutely sure? This will delete ALL screenshots.
                        </p>
                        <div className="flex space-x-3">
                          <button
                            onClick={handlePurgeStorage}
                            disabled={purgeMutation.isPending}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {purgeMutation.isPending ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              'Yes, Delete Everything'
                            )}
                          </button>
                          <button
                            onClick={() => setShowConfirmPurge(false)}
                            disabled={purgeMutation.isPending}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Automated Cleanup Info */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-lg">🔄</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-sm font-medium text-blue-800">
                    Automated Cleanup
                  </h4>
                  <p className="mt-1 text-sm text-blue-700">
                    The system automatically purges storage at configurable intervals during the testing phase. 
                    This helps maintain storage limits and ensures fresh test data.
                  </p>
                  <div className="mt-2 text-xs text-blue-600">
                    <strong>Last automated cleanup:</strong> {
                      metrics?.lastCleanup 
                        ? new Date(metrics.lastCleanup).toLocaleString()
                        : 'Never'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Guidelines */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Storage Guidelines
          </h3>
          <div className="prose text-sm text-gray-600">
            <ul className="space-y-2">
              <li>• Screenshots are automatically optimized to reduce storage usage</li>
              <li>• Images are resized to a maximum of 1920px on the longest side</li>
              <li>• JPEG compression is applied with 85% quality for optimal balance</li>
              <li>• All data is encrypted at rest in Cloudflare R2</li>
              <li>• Regular cleanup helps maintain performance and cost efficiency</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StoragePage